import {
  differenceInDays,
  differenceInSeconds,
  format,
  isToday,
  isTomorrow,
  isYesterday,
} from 'date-fns';

/**
 * Formats a countdown display from a target date
 * Returns formats like: "2 Days", "1 Day", "6h:42m:07s", "42m:07s", "07s"
 * If >= 24 hours, shows only days (e.g., "2 Days")
 * If < 24 hours, shows detailed countdown (e.g., "6h:42m:07s")
 */
export const formatCountdown = (targetDate: Date): string => {
  const now = new Date();
  const totalSeconds = differenceInSeconds(targetDate, now);

  if (totalSeconds <= 0) {
    return '0s';
  }

  const days = Math.floor(totalSeconds / (24 * 60 * 60));
  const hours = Math.floor((totalSeconds % (24 * 60 * 60)) / (60 * 60));
  const minutes = Math.floor((totalSeconds % (60 * 60)) / 60);
  const seconds = totalSeconds % 60;

  // If time span is >= 24 hours, just show days
  if (days > 0) {
    return days === 1 ? '1 Day' : `${days} Days`;
  }

  // Otherwise show detailed countdown
  const parts: string[] = [];

  if (hours > 0) {
    parts.push(`${hours}h`);
  }
  if (hours > 0 || minutes > 0) {
    parts.push(`${minutes.toString().padStart(2, '0')}m`);
  }
  parts.push(`${seconds.toString().padStart(2, '0')}s`);

  return parts.join(':');
};

/**
 * Formats a relative time display
 * Returns formats like: "2 Days", "Tomorrow", "Today", etc.
 */
export const formatRelativeTime = (targetDate: Date): string => {
  // Validate date
  if (!targetDate || isNaN(targetDate.getTime())) {
    return 'Date TBD';
  }

  if (isToday(targetDate)) {
    return 'Today';
  }

  if (isTomorrow(targetDate)) {
    return 'Tomorrow';
  }

  if (isYesterday(targetDate)) {
    return 'Yesterday';
  }

  const now = new Date();
  const diffInDays = differenceInDays(targetDate, now);

  // Additional check for NaN result
  if (isNaN(diffInDays)) {
    return 'Date TBD';
  }

  if (diffInDays > 0) {
    return `${diffInDays} Days`;
  } else {
    return `${Math.abs(diffInDays)} Days Ago`;
  }
};

/**
 * Formats a date for daily breakdown display
 * Returns formats like: "Jan 24", "Feb 01", etc.
 */
export const formatDayMonth = (date: Date): string => {
  return format(date, 'MMM dd');
};

/**
 * Formats a full date display
 * Returns formats like: "SAT FEB 01", "MON MAR 15", etc.
 */
export const formatGameTitle = (date: Date): string => {
  return format(date, 'EEE MMM dd').toUpperCase();
};

/**
 * Formats time remaining until a date
 * Returns different formats based on time remaining
 */
export const formatTimeRemaining = (targetDate: Date): string => {
  const now = new Date();

  // If date is in the past, return "Live"
  if (targetDate < now) {
    return 'Live';
  }

  return formatCountdown(targetDate);
};
